<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import BotPersonaCard from './BotPersonaCard.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const personas = computed(() => store.getters['botPersonas/getPersonas']);
const uiFlags = computed(() => store.getters['botPersonas/getUIFlags']);
const accountId = computed(() => store.getters['auth/getCurrentAccountId']);

const activeCount = computed(() => personas.value.filter(p => p.active).length);

onMounted(() => store.dispatch('botPersonas/fetch'));

function goToCreate() {
  router.push({
    name: 'bot_personas_new',
    params: { accountId: accountId.value },
  });
}

async function onDuplicate(persona) {
  const { id, created_at, updated_at, ...rest } = persona;
  await store.dispatch('botPersonas/create', {
    ...rest,
    name: `${persona.name} (Copy)`,
    active: false,
  });
  useAlert(t('MINISTRY.BOT_PERSONAS.DUPLICATE_SUCCESS'));
}

async function onDelete(persona) {
  if (!window.confirm(t('MINISTRY.BOT_PERSONAS.DELETE_CONFIRM'))) return;
  await store.dispatch('botPersonas/delete', persona.id);
  useAlert(t('MINISTRY.BOT_PERSONAS.DELETE_SUCCESS'));
}
</script>

<template>
  <div class="flex flex-col h-full bg-[#F5F3EE]">
    <!-- Page header -->
    <div class="bg-white border-b border-[#E8E6E0] px-10 py-5">
      <div class="flex items-start justify-between">
        <div>
          <h1 class="text-[22px] font-bold text-[#1A1A2E] leading-tight">
            {{ t('MINISTRY.BOT_PERSONAS.TITLE') }}
          </h1>
          <p class="text-[16px] text-slate-500 mt-0.5">
            {{
              t('MINISTRY.BOT_PERSONAS.ACTIVE_SUBTITLE', {
                count: activeCount,
                total: personas.length,
              })
            }}
          </p>
        </div>
        <div class="flex gap-2">
          <button
            class="px-4 py-2 border border-[#E8E6E0] text-[#6B7280] text-[13px] font-semibold rounded-lg hover:bg-[#F5F3EE] transition-colors"
          >
            {{ t('MINISTRY.BOT_PERSONAS.IMPORT_TEMPLATE') }}
          </button>
          <button
            class="flex items-center gap-1.5 px-4 py-2 bg-[#1B2A4A] text-white text-[13px] font-semibold rounded-lg hover:bg-[#243660] transition-colors"
            @click="goToCreate"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('MINISTRY.BOT_PERSONAS.NEW_PERSONA') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-10 py-8">
      <!-- Loading -->
      <div v-if="uiFlags.isFetching" class="flex justify-center py-16">
        <span class="i-lucide-loader-2 size-6 animate-spin text-slate-400" />
      </div>

      <!-- Grid -->
      <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <BotPersonaCard
          v-for="persona in personas"
          :key="persona.id"
          :persona="persona"
          :account-id="accountId"
          @duplicate="onDuplicate"
          @delete="onDelete"
        />

        <!-- Create New Persona card -->
        <button
          class="bg-[#FAFAF8] border-[1.5px] border-dashed border-[#D1CEC7] rounded-xl p-5 flex flex-col items-center justify-center gap-3 min-h-[200px] hover:border-[#C9A84C] hover:bg-[#FFF8E6] transition-colors group"
          @click="goToCreate"
        >
          <div
            class="size-12 rounded-full border-2 border-dashed border-[#D1CEC7] group-hover:border-[#C9A84C] flex items-center justify-center transition-colors"
          >
            <span
              class="i-lucide-plus size-5 text-slate-400 group-hover:text-[#C9A84C] transition-colors"
            />
          </div>
          <div class="text-center">
            <p class="text-[14px] font-semibold text-[#1A1A2E]">
              {{ t('MINISTRY.BOT_PERSONAS.CREATE_NEW') }}
            </p>
            <p class="text-[12px] text-slate-400 mt-0.5">
              {{ t('MINISTRY.BOT_PERSONAS.CREATE_NEW_SUBTITLE') }}
            </p>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>
